import React, { useEffect, useRef, useState } from "react";
import { lineItemsApi, usersApi } from "../services/makerCheckerApi";
import { Toast } from "../../../shared/Toast";
import { buildComparisonGroups } from "../payload/buildComparisonGroups";
import ComparisonTable from "../payload/ComparisonTable";

// maker_checker_logs.rej_reason is VARCHAR(100); keep the client within that.
const REJ_REASON_MAX = 100;

export const handleRejectWithPrompt = async (mc_id, ref_table_name, onSuccess) => {
  if (!mc_id) {
    Toast("error", "Invalid Request");
    return;
  }
  const entered = prompt("Enter reject reason:");
  if (!entered) return;
  const reason = entered.trim().slice(0, REJ_REASON_MAX);
  if (!reason) {
    Toast("error", "Please enter a rejection reason");
    return;
  }
  let res ;
  if(ref_table_name === 'lineitems') {
    res = await lineItemsApi.reject(mc_id, reason);
  } else {
    res = await usersApi.reject(mc_id, reason);
  }
  if (res.status === "SUCCESS") {
    Toast("success", "Request Rejected Successfully");
    if (onSuccess) onSuccess();
  } else {
    Toast("error", res.data?.message);
  }
};
export const handleApprove = async (mc_id, ref_table_name, onSuccess) => {
  if (!mc_id) {
    Toast("error", "Invalid Request");
    return;
  }
  let res ;
  if(ref_table_name === 'lineitems') {
    res = await lineItemsApi.approve(mc_id);
  } else {
    res = await usersApi.approve(mc_id);
  }
  if (res.status === "SUCCESS") {
    Toast("success", "Request Approved Successfully");
    if (onSuccess) onSuccess();
  } else {
    Toast("error", res.data?.message);
  }
};
const DECIDED_STATUS = { 1: { label: "Approved", cls: "bg-emerald-100 text-emerald-800", icon: "✓" },
                         2: { label: "Rejected", cls: "bg-red-100 text-red-800", icon: "✕" } };

export const ApproveRejectModal = ({ mcAction, onApprove, onReject, performApprove, performReject, readOnly = false }) => {
  const [acting, setActing] = useState(false);

  const decided = DECIDED_STATUS[mcAction?.status];
  if (decided) {
    return (
      <div className="flex justify-end p-4">
        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${decided.cls}`}>
          <span aria-hidden="true">{decided.icon}</span>
          This request was already {decided.label.toLowerCase()}
        </span>
      </div>
    );
  }

  if (readOnly) return null;

  // Guard against a double submit: both handlers are async and the buttons stay mounted.
  const run = async (fn) => {
    if (acting) return;
    setActing(true);
    try {
      await fn();
    } finally {
      setActing(false);
    }
  };

  const doApprove = () =>
    performApprove ? performApprove(mcAction) : handleApprove(mcAction?.mc_id, mcAction.ref_table_name, () => onApprove?.());
  const doReject = () =>
    performReject ? performReject(mcAction) : handleRejectWithPrompt(mcAction?.mc_id, mcAction.ref_table_name, () => onReject?.());

  return (
    <div className="flex justify-end gap-2 p-4">
      <button
        disabled={acting}
        onClick={() => run(doApprove)}
        className="px-4 py-2 text-white bg-green-500 hover:bg-green-600 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {acting ? "Processing…" : "Approve"}
      </button>
      <button
        disabled={acting}
        onClick={() => run(doReject)}
        className="px-4 py-2 text-white bg-red-500 hover:bg-red-600 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Reject
      </button>
    </div>
  );
};
const PayloadModal = ({ mcAction, isOpen, onClose, performApprove, performReject, readOnly }) => {
  const dialogRef = useRef(null);


  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKeyDown);
    dialogRef.current?.focus();
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;


  const groups = buildComparisonGroups(mcAction);

  return (
    <div className="fixed inset-0 flex items-end sm:items-center justify-center bg-black bg-opacity-50 z-50 transition-opacity" onClick={onClose}>
      <div
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label="Payload comparison"
        className="relative w-full sm:max-w-3xl max-h-[85vh] bg-white rounded-t-xl sm:rounded-xl shadow-2xl overflow-auto outline-none"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 flex justify-between items-center bg-white border-b border-gray-200 px-4 py-3 sm:rounded-t-xl z-10">
          <h2 className="text-lg font-semibold text-gray-800">
            {mcAction?.status === 0 ? "Requested changes" : "What changed"}
          </h2>
          <button onClick={onClose} aria-label="Close" className="text-gray-500 hover:text-gray-800 transition-colors text-xl font-bold px-2">
            &times;
          </button>
        </div>

        <div className="p-4">
          <ComparisonTable groups={groups} />
        </div>
        <ApproveRejectModal
          mcAction={mcAction}
          performApprove={performApprove}
          performReject={performReject}
          readOnly={readOnly}
        />
      </div>
    </div>
  );
};

export default PayloadModal;
